/**
 * BulkSkippedDialog
 *
 * "Details" of a bulk send / release: lists every skipped item with the localised reason.
 */

import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { bulkReasonText } from '@/utils/bulkReleaseSummary';
import type { BulkItemResult } from '@/types/approval';

interface BulkSkippedDialogProps {
  items: BulkItemResult[] | null;
  onClose: () => void;
  /** Display name of a post (title or caption excerpt); null falls back to "Post #id" */
  labelFor: (postId: number) => string | null;
}

export const BulkSkippedDialog = ({ items, onClose, labelFor }: BulkSkippedDialogProps) => {
  const { t } = useTranslation('common');
  return (
    <Modal
      open={items !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t('release_bulk_details_title')}
      size="md"
      footer={<Button onClick={onClose}>{t('close')}</Button>}
    >
      <ul className="divide-y divide-gray-100">
        {(items ?? []).map((item) => (
          <li key={`${item.postId}-${item.variationId ?? 'base'}`} className="py-2.5">
            <p className="truncate text-sm font-medium text-gray-900">
              {labelFor(item.postId) ?? t('release_bulk_item_fallback', { id: item.postId })}
              {item.variationId !== null && (
                <span className="ml-2 rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-800">
                  {t('release_bulk_item_variation')}
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-gray-600">{bulkReasonText(t, item.reasonCode)}</p>
          </li>
        ))}
      </ul>
    </Modal>
  );
};

export default BulkSkippedDialog;
